import React from 'react';
import i18n from 'util/i18n';
import { HISTORY, HISTORY_RESIDENCE } from 'config/formSections/history';

import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Subsection from 'components/Section/shared/Subsection';

import Accordion, { openState } from 'components/Form/Accordion/Accordion';
import { EmptyListInstance } from 'forms/shared/models';
import { ff } from 'helpers/featureFlags';
import Comments from 'components/Form/Comments';
import { sortItemsByDate } from 'util/general';
import Button from 'components/Form/Button';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { residenceAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import Gap from '../Gap';
import ResidenceItem from './ResidenceItem';
import { ResidenceCustomSummary } from '../summaries';

const sectionConfig = {
  key: HISTORY_RESIDENCE.key,
  section: HISTORY.name,
  store: HISTORY.store,
  subsection: HISTORY_RESIDENCE.name,
  storeKey: HISTORY_RESIDENCE.storeKey,
};

const byline = (item, index, initial, translation, required, isValid) => {
  switch (true) {
    // If item is required and not currently opened and is not valid, show message
    case required && !item.open && !isValid:
    case !item.open && !initial && item.Item && !isValid:
      return (
        <div className={`byline ${openState(item, initial)} fade in`.trim()}>
          <div className="usa-alert usa-alert-error" role="alert">
            <div className="usa-alert-body">
              <h5 className="usa-alert-heading">{i18n.m(translation)}</h5>
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export class Residence extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
    this.update = this.update.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.updateComments = this.updateComments.bind(this);
  }

  handleUpdate = (values) => {
    this.update({
      List: values,
    });
  };

  updateComments(values) {
    this.update({
      Comments: values,
    });
  }

  update(queue) {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      Comments: this.props.Comments,
      ...queue,
    });
  }

  updateSort = () => {
    const { List } = this.props;
    const newList = { ...List, items: sortItemsByDate(List.items) };
    this.update({ List: newList });
  };

  sortSelector = () => (
    <Button
      classContent="default-sort"
      className="default-sort-button"
      onClick={() => this.updateSort()}
      light
      ignorePaddingMargin
      title="Sort by most recent"
    >
      <span>Sort</span>
    </Button>
  );

  customResidenceByline = (item, index, initial) => {
    const { required, errors, overrideInitial } = this.props;

    const newInitial = overrideInitial ? false : initial;
    const itemHasErrors = errors && errors.filter((e) => e.indexOf(item.uuid) > -1).length > 0;

    return byline(
      item,
      index,
      newInitial,
      'history.residence.collection.summary.incomplete',
      required,
      !itemHasErrors
    );
  };

  render() {
    const {
      List,
      errors,
      formErrors,
      totalYears,
      gaps,
      requireHistoryResidenceRole,
      requireHistoryResidenceReferenceInfo,
      requireHistoryResidenceReferenceName,
      features,
      peopleWhoKnowYou
    } = this.props;
    const { branch, items = [] } = List;

    // Deprecated with EN-4733 & EN-5264
    const showGapError = !ff(features).getActive('EN-4733')
      ? branch
      && branch.value === 'No'
      && gaps.length > 0
      && formErrors.filter((e) => e.code.includes('error.gap.historyResidence')).length > 0
      : false;

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(items, peopleWhoKnowYou, residenceAddressNameFields);

    const gapError = (
      <Gap
        title={i18n.t('history.residence.gap.title')}
        para={i18n.t('history.residence.gap.para', { years: totalYears })}
        gaps={gaps}
      />
    );

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div
        className="section-content residence"
        data-section={HISTORY.key}
        data-subsection={HISTORY_RESIDENCE.key}
      >
        {this.sortSelector()}
        <Accordion
          scrollToTop={this.props.scrollToTop}
          defaultState={this.props.defaultState}
          {...this.props.List}
          sort={this.props.sort}
          realtime={this.props.realtime}
          onUpdate={this.handleUpdate}
          onError={this.handleError}
          caption={this.props.caption}
          byline={this.customResidenceByline}
          customSummary={ResidenceCustomSummary}
          description={i18n.t('history.pvq.residence.collection.summary.title')}
          appendTitle={i18n.t('history.pvq.residence.collection.appendTitle')}
          appendLabel={i18n.t('history.pvq.residence.collection.append')}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          errors={accordionErrors}
          additionalErrors={
            (showGapError && gapError)
            || (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
            || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
          }
          ignoreInitialScrollTo
        >
          <ResidenceItem
            bind
            name="Item"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            formType={this.props.formType}
            formVersion={this.props.formVersion}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            requireHistoryResidenceRole={requireHistoryResidenceRole}
            requireHistoryResidenceReferenceInfo={requireHistoryResidenceReferenceInfo}
            requireHistoryResidenceReferenceName={requireHistoryResidenceReferenceName}
          />
        </Accordion>
      </div>
    ) : (
      <div
        className="section-content residence"
        data-section={HISTORY.key}
        data-subsection={HISTORY_RESIDENCE.key}
      >
        {this.sortSelector()}
        <Accordion
          scrollToTop={this.props.scrollToTop}
          defaultState={this.props.defaultState}
          {...this.props.List}
          sort={this.props.sort}
          realtime={this.props.realtime}
          onUpdate={this.handleUpdate}
          onError={this.handleError}
          caption={this.props.caption}
          byline={this.customResidenceByline}
          customSummary={ResidenceCustomSummary}
          description={i18n.t('history.residence.collection.summary.title')}
          appendTitle={i18n.t('history.residence.collection.appendTitle')}
          appendLabel={i18n.t('history.residence.collection.append')}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          errors={accordionErrors}
          additionalErrors={
            (showGapError && gapError)
            || (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
            || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
          }
          ignoreInitialScrollTo
        >
          <ResidenceItem
            bind
            name="Item"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            formType={this.props.formType}
            formVersion={this.props.formVersion}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            requireHistoryResidenceRole={requireHistoryResidenceRole}
            requireHistoryResidenceReferenceInfo={requireHistoryResidenceReferenceInfo}
            requireHistoryResidenceReferenceName={requireHistoryResidenceReferenceName}
          />
        </Accordion>
        <Comments
          title={i18n.t('history.residence.comments')}
          name="Comments"
          className="optional-comments"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

Residence.defaultProps = {
  List: EmptyListInstance,
  scrollToTop: '',
  defaultState: true,
  realtime: false,
  required: false,
  sort: null,
  Comments: {},
  totalYears: 10,
  requireHistoryResidenceRole: true,
  requireHistoryResidenceReferenceInfo: true,
  overrideInitial: false,
  caption: null,
  onUpdate: () => { },
  onError: (value, arr) => arr,
  addressBooks: {},
  dispatch: () => { },
  errors: [],
  gaps: [],
};

export default connectSubsection(Residence, sectionConfig);
